import { get } from 'svelte/store'
import { getOrderDetails } from '@lib/orders'
import { fn, pageOptsStore } from '@packages/stores'
import { Pages } from '@packages/utils/enums'
import type { TSubscriptionPurchasePageOpts } from '@packages/utils/types'
import { createSubscription } from '@src/passport/subscription'
import { configStore } from '@src/stores/config'
import type { TPaymentAsset } from '../payment.constants'
import { afterPaymentProcess } from '../Payment.service'
import { createOrder, processOrder } from '../order.service'

export type TStripePaymentContext = unknown
export type TStripeAfterConfirmArgs = {
  context?: TStripePaymentContext
  priceCents: number
}

export type TStripePaymentFlow = {
  createPayment: () => Promise<{ clientSecret: string; context?: TStripePaymentContext }>
  afterConfirm: (args: TStripeAfterConfirmArgs) => Promise<void>
  showWalletPay?: boolean
}

const getSubscriptionOpts = () => get(pageOptsStore) as TSubscriptionPurchasePageOpts
const getPurchaseConfig = () => get(pageOptsStore) as { assets: TPaymentAsset[]; title: string; subtitle: string }

const getSubscriptionMembershipResourceId = () => {
  const opts = getSubscriptionOpts()
  return opts.subscription?.membershipResourceId
}

const getSubscriptionTokenId = () => {
  const opts = getSubscriptionOpts()
  return opts.subscription?.tokenId
}

export const createOrderStripeFlow = (idempotencyKey: string): TStripePaymentFlow => ({
  createPayment: async () => {
    const purchaseConfig = getPurchaseConfig()
    const { assets } = purchaseConfig
    const config = get(configStore)
    if (!assets?.length) throw new Error('No purchase config assets found')

    const orderId = await createOrder(
      {
        assets,
        payment_provider: 'stripe',
        client_id: config.clientId,
        currency: 'USD',
      },
      idempotencyKey,
    )
    const order = await getOrderDetails(config.credenza.generalApiUrl, orderId)
    const clientSecret = order.payment_provider_data?.client_secret
    if (!clientSecret) throw new Error('Payment is unavailable now')

    return { clientSecret, context: orderId }
  },
  afterConfirm: async ({ context, priceCents }) => {
    const purchaseConfig = getPurchaseConfig()
    const orderId = context as string
    const res = await processOrder(orderId)
    await afterPaymentProcess({ res, priceCents, purchaseConfig })
  },
  showWalletPay: true,
})

export const createSubscriptionStripeFlow = (idempotencyKey: string): TStripePaymentFlow => ({
  createPayment: async () => {
    const opts = getSubscriptionOpts()
    const membershipResourceId = getSubscriptionMembershipResourceId()

    if (!membershipResourceId) throw new Error('Missing subscription membership resource id')

    const subscription = await createSubscription(
      {
        membership_resource_id: membershipResourceId,
        token_id: getSubscriptionTokenId(),
        chain_id: opts.subscription?.chainId,
      },
      idempotencyKey,
    )
    const clientSecret = subscription.payment_provider_data?.client_secret
    if (!clientSecret) throw new Error('Subscription payment is unavailable now')

    return { clientSecret, context: subscription }
  },
  afterConfirm: async () => {
    const opts = getSubscriptionOpts()
    const { toastAlert, openUI } = get(fn)

    toastAlert('Your subscription was created successfully')
    pageOptsStore.set({
      title: opts.successTitle || 'Success!',
      subtitle: opts.successSubtitle || 'Your subscription was created successfully.',
      receipt: false,
    })
    await openUI(Pages.PAYMENT_RESULT)
  },
  showWalletPay: false,
})
